import _ from 'lodash';
import moment from 'moment';

import { SORTABLE_TIME_FORMAT } from 'constants';
import { getDateRangeFilter } from 'utils/filter-utils';
import Call from 'services/data/call';
import Email from 'services/data/email';
import MonitoringAlert from 'services/data/monitoring-alert';
import Rest from 'services/data/rest';
import axios from 'axios';


const statusMap = {
  Active: 4,
  'After Hours': 3,
  Resolved: 2,
  Archived: 2
};

export default class Triage extends Rest {
  static path = 'triage';
  static defaultQuery = {
    sort: 'createdAt DESC'
  };

  static findFast(query, options = {}) {
    if (!options.cancelToken) options.cancelToken = this.getCancelToken(options);

    return Promise.resolve(axios.get(`/api/triage-fast`, _.defaults({ params: query }, options)))
      .tap(this.logRequest)
      .then((response) => response.data)
      .catch(axios.isCancel, _.noop)
      .finally(() => Rest.releaseCancelToken(options.cancelToken));
  }

  /**
   * The live triage list, pulled rather than pushed.
   *
   * The server used to broadcast this entire list over the `triage` socket on
   * every write. It now broadcasts only a version number, and this fetches the
   * payload — served out of Redis with an ETag, so an unchanged version costs a
   * 304 with no body. The browser handles the revalidation itself;
   * `Cache-Control: no-cache` is what makes it ask rather than assume.
   *
   * Takes no query parameters on purpose. This is one fixed shape, which is what
   * lets it be cached at all — filtered queries still go through `find` to
   * `GET /api/triage`.
   *
   * Returns the version alongside the items so the caller can apply a monotonic
   * guard. It comes from the response, not from the socket message that triggered
   * the fetch: on a 304 the browser replays the cached response and its headers,
   * so this reports the version of the body actually in hand.
   *
   * `version` is null when the server had nothing cached and queried directly
   * (worker down, or no Redis locally). That means "freshly queried, definitely
   * current", so the guard has to skip rather than compare — treating it as 0
   * would make the caller discard the newest data it has.
   */
  static async findCached(options = {}) {
    const response = await axios.get('/api/triage-cache', options);

    this.logRequest(response);

    const version = response.headers['x-triage-version'];

    return {
      items: response.data,
      version: version === undefined ? null : Number(version)
    };
  }

  static getSortKey(item) {
    const status = _.get(statusMap, item.status, 1);
    const claim = !item.claimedBy || _.isEmpty(item.claimedBy) ? 2 : 1;
    const date = moment(item.createdAt).format(SORTABLE_TIME_FORMAT);

    // Ignore claim if the item is open
    return `${status}-${status > 2 ? claim : 0}-${date}`;
  }

  static mapFilters(filters) {
    const {
      triageType,
      triageStatus,
      id,
      ticket,
      company,
      date,
      claimedBy,
      contact,
      subject,
      from,
      hostname,
      trigger,
      team,
      phoneNumber
    } = filters;

    // If the id filter is applied, ignore all other filters
    // (and turn off the api's default status filtering)
    if (id) return { id, status: { '!': null } };

    return {
      company,
      ticket,
      'company.team': team,
      subject: subject ? { contains: subject } : undefined,
      from: (from || phoneNumber) ? { contains: from || phoneNumber.replace(/\D/g, '') } : undefined,
      trigger: trigger ? { contains: trigger } : undefined,
      hostname: hostname ? { contains: hostname } : undefined,
      fromPerson: contact ? { in: contact } : undefined,
      status: triageStatus ? { in: triageStatus } : undefined,
      createdAt: getDateRangeFilter(date),
      type: triageType ? { in: triageType } : undefined,
      claimedBy: claimedBy ? { in: claimedBy } : undefined
    };
  }

  static update(values) {
    const { triageId, ...data } = values;
    const [type, id] = _.split(triageId, '-');
    const update = { ...data, id };

    if (type === 'call') return Call.update(update);
    if (type === 'email') return Email.update(update);
    if (type === 'monitoringalert') return MonitoringAlert.update(update);
  }

  // Add the triage item to a ticket
  static addToTicket(values, { updateTriageItem, triageItem, currentUser }) {
    return updateTriageItem({
      id: triageItem.id,
      // If a specific ticket was provided, set to that ticket's id, otherwise create a new ticket
      // using description, company, agreement set in form
      ticket: values.ticket ? values.ticket : {
        ...values,
        tasks: [{
          description: 'Triage',
          priority: 'Critical',
          dueDate: moment().endOf('day').toDate(),
          assignedTo: [{ id: currentUser.id }]
        }],
        createdBy: currentUser.id,
        expectedCompletion: moment().add(1, 'day').startOf('day').toDate()
      },
      company: values.company,
      status: 'Resolved'
    });
  }

  // Resolve a triage item with a time report
  static resolveWithTime(values, { updateTriageItem, triageItem, currentUser }) {
    return updateTriageItem({
      id: triageItem.id,
      company: values.company,
      status: 'Resolved',
      ticket: values.ticket ? values.ticket : {
        timeDefaultBillable: values.timeDefaultBillable,
        description: values.ticketDescription,
        company: values.company,
        agreement: values.agreement,
        timeReports: [{
          description: values.timeReportDescription,
          duration: values.timeReportDuration,
          createdBy: currentUser.id,
          agreement: values.agreement,
          date: moment().toDate(),
          rate: values.rate,
          afterHours: values.afterHours,
          afterHoursPremium: values.afterHours ? values.afterHoursPremium : 0
        }],
        createdBy: currentUser.id,
        resolution: `[Populated by QR time report] ${values.timeReportDescription}`,
        tasks: [{
          description: 'Resolve',
          status: 'Complete',
          dueDate: moment().endOf('day').toDate(),
          assignedTo: [{ id: currentUser.id }]
        }]
      }
    });
  }
}
